import { CSSProperties, useState, useEffect, useContext } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box } from "@mui/material";
import { useParams } from "react-router-dom";
import { CurrentUserContext } from "../context/RequireAuth";
import { dateFnsLocalizer, View } from "react-big-calendar";
import format from "date-fns/format";
import parse from "date-fns/parse";
import startOfWeek from "date-fns/startOfWeek";
import getDay from "date-fns/getDay";
import { utcToZonedTime, format as formatTz } from "date-fns-tz";
import { EventNotice, EventNotice_NoticeType } from "protogen/calendar_pb";
import CalendarComponent from "./CalendarComponent";
import { CalendarFiltersDelegate } from "./FiltersDelegate";
import AddEventDialog from "./AddEventDialog";
import { CalendarFilter, CalendarSettings } from "./CustomCalendarSettings";
import EditEventDialog from "./EditEventDialog";
import ViewEventDialog from "./ViewEventDialog";
import { EphemeralEvent } from "types/calendars";
import LoadingOverlay from "./LoadingOverlay";
import useDateParams from "components/hooks/calendar/useDateParams";
import useIsMobile from "../hooks/useIsMobile";
import { Family } from "protogen/advisors_service_pb";
import CalendarSearchDialog from "./CalendarSearchDialog";

const LOCALES = {
  "en-US": require("date-fns/locale/en-US"),
};

interface Props {
  events: EventNotice[];
  accountType: "advisor" | "member";
  familyRef?: string;
  embeddedMode?: boolean; // Minimizes toolbar.
  // more general fetcher?
  refreshEvents?: (includeDuplicates?: boolean) => Promise<void>;
  // filters - maybe expand to apply filters?
  calendarFilters?: CalendarFilter[];
  setEnabledFilters?: (filters: CalendarFilter[]) => void;
  getEventStyles?: (event: EventNotice) => CSSProperties | null;
  handleNavigate?: (newDate: Date) => Promise<void>;
  loading?: boolean;
  families?: Family[];
  headerViewType?: "compact" | "normal";
  refreshCalendar?: () => Promise<void>;
  viewTimezone?: string;
  onTimezoneChange?: (timezone: string) => void;
  secondaryTimezone?: string;
  dualTimezoneEnabled?: boolean;
  onDualTimezoneChange?: (dualEnabled: boolean, secondaryTz?: string) => void;
  searchEvents?: (query: string) => Promise<EventNotice[]>;
}

export default ({
  events,
  accountType,
  familyRef,
  embeddedMode = false,
  refreshEvents,
  calendarFilters,
  setEnabledFilters,
  getEventStyles,
  handleNavigate,
  loading = false,
  families,
  headerViewType = "normal",
  refreshCalendar,
  viewTimezone,
  onTimezoneChange,
  secondaryTimezone,
  dualTimezoneEnabled,
  onDualTimezoneChange,
  searchEvents,
}: Props) => {
  let params = useParams();
  const location = useLocation();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const defaultDate = useDateParams();
  const currentUser = useContext(CurrentUserContext);
  const currentFamily = familyRef
    ? families?.find((f) => f.ref === familyRef)
    : null;
  const [refreshing, setRefreshing] = useState(false);
  const viewFromParams: View = (queryParams.get("view") || "week") as View;
  const [hasLoadedEventRef, setHasLoadedEventRef] = useState(false);
  const [menuAnchorPosition, setMenuAnchorPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<EventNotice | undefined>(
    undefined,
  );
  const [initialEvent, setInitialEvent] = useState<EphemeralEvent | undefined>(
    undefined,
  );
  const displayTimezone =
    viewTimezone ||
    currentUser?.timezone ||
    Intl.DateTimeFormat().resolvedOptions().timeZone;

  const localizer = dateFnsLocalizer({
    format: (date: Date, formatStr: string, _culture?: string) => {
      try {
        const isTimeFormat =
          /[Hhmsa]/.test(formatStr) && !/[dyM]/.test(formatStr);

        if (displayTimezone && displayTimezone !== "UTC" && isTimeFormat) {
          const zonedDate = utcToZonedTime(date, displayTimezone);
          return formatTz(zonedDate, formatStr, { timeZone: displayTimezone });
        }
        return format(date, formatStr);
      } catch (error) {
        return format(date, formatStr);
      }
    },
    parse,
    startOfWeek,
    getDay,
    locales: LOCALES,
  });
  const handleNewEvent = () => {
    let start;
    const dateParams = new URLSearchParams(window.location.search);
    if (
      dateParams.get("month") &&
      dateParams.get("day") &&
      dateParams.get("year")
    ) {
      start = new Date(
        parseInt(dateParams.get("year") || ""),
        parseInt(dateParams.get("month") || ""),
        parseInt(dateParams.get("day") || ""),
      );
    }
    setInitialEvent({ familyRef, startDate: start });
    setAddModalOpen(true);
  };
  const handleRefresh = async () => {
    setRefreshing(true);
    await refreshCalendar?.();
    await refreshEvents?.();
    setRefreshing(false);
  };
  const handleSelectSlot = ({ start, end }: { start: Date; end: Date }) => {
    const isAllDay =
      start.getHours() === 0 &&
      start.getMinutes() === 0 &&
      end.getHours() === 0 &&
      end.getMinutes() === 0;
    setInitialEvent({
      allDay: isAllDay,
      startDate: start,
      endDate: isAllDay ? new Date(end.setDate(end.getDate() - 1)) : end,
      familyRef,
    });
    setAddModalOpen(true);
  };
  const updatePath = (path: string) => {
    const currentUrl = window.location.href;
    const url = new URL(currentUrl);
    url.pathname = path;
    window.history.pushState({}, "", url.toString());
  };

  const handleSelectEvent = (event: EventNotice) => {
    if (
      event.noticeType == EventNotice_NoticeType.NoticeType_UPCOMING_TASK ||
      event.noticeType == EventNotice_NoticeType.NoticeType_OVERDUE_TASK
    ) {
      navigate(`/tasks/${encodeURIComponent(event.taskRef)}`);
      return;
    }
    setSelectedEvent(event);
    setViewModalOpen(true);
    const isExternalEvent =
      event.noticeType === EventNotice_NoticeType.NoticeType_EXTERNAL_EVENT;
    updatePath(
      `calendar/${event.eventRef}${isExternalEvent ? "?external=true" : ""}`,
    );
  };

  const onEnableEdit = () => {
    setViewModalOpen(false);
    setEditModalOpen(true);
  };
  const handleSettingsOpen = (event: React.MouseEvent<HTMLElement>) => {
    const targetRect = event.currentTarget.getBoundingClientRect();
    setMenuAnchorPosition({
      // Flipped because we want to anchor to the bottom right.
      top: targetRect.bottom,
      left: targetRect.left,
    });
  };
  const handleSettingsClose = () => {
    setMenuAnchorPosition(null);
  };

  const selectEvent = (eventRef: string) => {
    if (!hasLoadedEventRef && events.length > 0) {
      let event = events.find((e) => e.eventRef === eventRef);
      if (!event) {
        // This is a Faye event and we have not loaded it yet.
        // The view modal will fetch the individual event.
        event = new EventNotice({ eventRef: eventRef });
      }
      if (event) {
        setHasLoadedEventRef(true);
        setViewModalOpen(true);
        setSelectedEvent(event);
      }
    }
  };

  useEffect(() => {
    if (params.eventRef) {
      selectEvent(params.eventRef);
    }
  }, [params.eventRef, events]);

  return (
    <Box sx={{ position: "relative", height: "100%", width: "100%" }}>
      <CalendarComponent
        events={events}
        embeddedMode={embeddedMode}
        localizer={localizer}
        handleSelectEvent={handleSelectEvent}
        handleNewEvent={handleNewEvent}
        handleRefresh={handleRefresh}
        handleNavigate={handleNavigate}
        onSearchClick={searchEvents ? () => setSearchOpen(true) : undefined}
        handleSelectSlot={handleSelectSlot}
        getEventStyles={getEventStyles}
        viewTimezone={viewTimezone}
        onTimezoneChange={onTimezoneChange}
        currentFamily={currentFamily}
        secondaryTimezone={secondaryTimezone}
        dualTimezoneEnabled={dualTimezoneEnabled}
        onDualTimezoneChange={onDualTimezoneChange}
        filtersDelegate={
          calendarFilters?.length ? (
            <CalendarFiltersDelegate onClick={handleSettingsOpen} />
          ) : null
        }
        defaultDate={defaultDate}
        defaultView={isMobile ? "agenda" : viewFromParams}
        headerViewType={headerViewType}
      />
      <CalendarSettings
        menuOpen={!!menuAnchorPosition}
        handleClose={handleSettingsClose}
        disabled={false}
        calendarFilters={calendarFilters}
        setEnabledFilters={setEnabledFilters}
        anchorPos={menuAnchorPosition}
      />
      {searchEvents && (
        <CalendarSearchDialog
          open={searchOpen}
          onClose={() => setSearchOpen(false)}
          onSearch={searchEvents}
          familyNameByRef={Object.fromEntries(
            (families || []).map((family) => [family.ref, family.name]),
          )}
          onSelectEvent={(event) => {
            setSearchOpen(false);
            handleSelectEvent(event);
          }}
        />
      )}
      {initialEvent && (
        <AddEventDialog
          familyRef={familyRef}
          selectFamily={accountType === "advisor" && !familyRef}
          open={addModalOpen}
          onClose={() => {
            setAddModalOpen(false);
            setInitialEvent(undefined);
          }}
          onCreated={async () => {
            setAddModalOpen(false);
            await refreshEvents?.();
          }}
          initialFormData={initialEvent}
          accountType={accountType}
        />
      )}
      <ViewEventDialog
        open={viewModalOpen}
        onClose={async () => {
          setViewModalOpen(false);
          window.history.back();
        }}
        primaryAction={onEnableEdit}
        eventRef={selectedEvent?.eventRef || ""}
        accountType={accountType}
        eventNotice={selectedEvent}
        onDelete={async () => {
          await refreshEvents?.();
        }}
        onShowDuplicateEvents={() => {
          refreshEvents?.(true);
        }}
        family={families?.find(
          (family) => family.ref === selectedEvent?.familyRef,
        )}
      />
      <EditEventDialog
        open={editModalOpen}
        onClose={() => {
          setEditModalOpen(false);
          window.history.back();
        }}
        onEdited={async () => {
          setEditModalOpen(false);
          window.history.back();
          await refreshEvents?.();
        }}
        eventRef={selectedEvent?.eventRef || ""}
        accountType={accountType}
        notice={selectedEvent}
      />
      {(loading || refreshing) && <LoadingOverlay></LoadingOverlay>}
    </Box>
  );
};
